import * as React from "react";
import { cx } from "../internal/cx.js";

export type TopNavLayoutWidth = "default" | "wide" | "full";

const WIDTH_CLASS: Record<TopNavLayoutWidth, string> = {
  default: "cw-v2-top-nav-layout__main--default",
  wide: "cw-v2-top-nav-layout__main--wide",
  full: "cw-v2-top-nav-layout__main--full",
};

export interface TopNavLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Full-width header bar (logo + account actions). Inner content self-centers. */
  header?: React.ReactNode;
  /**
   * Horizontal tab strip shown below the header on desktop only (`lg` and up).
   * On smaller viewports the strip is hidden and `footerNav` takes over.
   */
  tabBar?: React.ReactNode;
  /** Sticky bottom nav for mobile (`lg:hidden`). Provide the nav; the template positions it. */
  footerNav?: React.ReactNode;
  /** Content max-width. `wide` (1200px) is the product default. */
  width?: TopNavLayoutWidth;
}

/**
 * Top-nav page shell — the benefits-wallet layout pattern, promoted as a sibling
 * to `DashboardLayout` (which is sidebar-first). Owns the
 * header → desktop tab strip → centered outlet → mobile footer-nav rhythm on the
 * shared tinted canvas ground, so surfaces that navigate by top tabs (rather than
 * a sidebar) don't each re-implement the shell.
 *
 * Slot-based and domain-free: apps pass their own header, tab bar, and footer
 * nav. Wrap the routed tree in `ThemeRoot` + `TooltipProvider` at the app root
 * as usual; this template is the inner shell.
 */
export const TopNavLayout = React.forwardRef<HTMLDivElement, TopNavLayoutProps>(
  function TopNavLayout(
    { children, className, footerNav, header, tabBar, width = "wide", ...props },
    ref,
  ) {
    return (
      <div ref={ref} className={cx("cw-v2-theme", "cw-v2-top-nav-layout", className)} {...props}>
        {header ? <div className="cw-v2-top-nav-layout__header">{header}</div> : null}
        {tabBar ? <div className="cw-v2-top-nav-layout__tabbar">{tabBar}</div> : null}
        <main className="cw-v2-top-nav-layout__body">
          <div className={cx("cw-v2-top-nav-layout__main", WIDTH_CLASS[width])}>{children}</div>
        </main>
        {footerNav ? <div className="cw-v2-top-nav-layout__footer-nav">{footerNav}</div> : null}
      </div>
    );
  },
);
TopNavLayout.displayName = "TopNavLayout";
